import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Logo } from "../components/Logo";
import { Notice, Progress } from "../components/Notice";
import { authErrorFrom, requestPasswordReset, type Audience } from "../lib/authProviders";
import { supabase } from "../lib/supabase";

const WAIT_MS = 12_000;
const NEUTRAL_SENT = "If a SoundLayer ID exists for that email, a reset link is on its way. Open the newest message from SoundLayer.";

/**
 * Where a password reset link returns (same origin as the request).
 *
 * Valid recovery is established only by supabase-js emitting
 * PASSWORD_RECOVERY after it exchanged the link's tokens for a session
 * (AuthProvider records it). A URL that merely carries "type=recovery",
 * an ordinary signed-in session, or a remembered session for a different
 * account never shows the password form. Expired, already-used and
 * invalid links arrive with Supabase's error parameters and get an
 * honest message plus a way to request a new link.
 */
export function RecoverPassword() {
  const { session, recovery, clearRecovery, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const audience: Audience = params.get("audience") === "dealer" ? "dealer" : "client";
  const signinPath = audience === "dealer" ? "/dealer/signin" : "/signin";
  const home = audience === "dealer" ? "/dealer" : "/";

  const linkError = authErrorFrom(location.search, location.hash, "reset");
  // Tokens in the hash mean supabase-js is exchanging them right now; wait
  // for the event before deciding the link was bad.
  const [tokensInUrl] = useState(() => /(^|[#&])access_token=/.test(location.hash));
  const [timedOut, setTimedOut] = useState(false);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ email: string | null } | null>(null);
  const [exchangeFailed, setExchangeFailed] = useState(false);

  const [requestEmail, setRequestEmail] = useState("");
  const [requestInfo, setRequestInfo] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  useEffect(() => {
    if (recovery || linkError || !tokensInUrl) return;
    // supabase-js reports the outcome of exchanging the link's tokens from
    // initialize(); a failed exchange is known at once. The timer is only a
    // fallback if that promise never settles.
    let active = true;
    supabase?.auth.initialize().then(({ error }) => {
      if (active && error) setExchangeFailed(true);
    });
    const id = window.setTimeout(() => active && setTimedOut(true), WAIT_MS);
    return () => {
      active = false;
      window.clearTimeout(id);
    };
  }, [recovery, linkError, tokensInUrl]);

  if (!supabase) {
    return (
      <section className="stack" style={{ maxWidth: 420 }}>
        <h1>Reset password</h1>
        <Notice kind="error">Sign-in is not configured on this deployment.</Notice>
      </section>
    );
  }
  const client = supabase;

  async function update(event: FormEvent) {
    event.preventDefault();
    if (!recovery) return;
    if (password.length < 8) {
      setError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // updateUser acts on whatever session supabase-js holds right now.
      // Confirm it is still the account from the reset link before sending
      // anything (another tab may have signed in as someone else meanwhile).
      const { data: current } = await client.auth.getSession();
      if (!current.session || current.session.user.id !== recovery.userId) {
        clearRecovery();
        setError("This reset no longer matches the signed-in account. Request a new link.");
        return;
      }
      const { data, error } = await client.auth.updateUser({ password });
      if (error) {
        setError(error.message);
        return;
      }
      if (data.user && data.user.id !== recovery.userId) {
        clearRecovery();
        setError("This reset no longer matches the signed-in account. Request a new link.");
        return;
      }
      const email = recovery.email;
      clearRecovery();
      setDone({ email });
    } finally {
      setBusy(false);
    }
  }

  async function requestNew(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setRequestError(null);
    setRequestInfo(null);
    try {
      const message = await requestPasswordReset(requestEmail, audience);
      if (message) setRequestError(message);
      else setRequestInfo(NEUTRAL_SENT);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <section className="stack auth" style={{ maxWidth: 420 }}>
        <div>
          <Logo size={56} className="logo-hero" />
          <h1>Password updated</h1>
          <p className="lede">You are signed in{done.email ? ` as ${done.email}` : ""}. Use the new password next time.</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => navigate(home, { replace: true })}>
          Continue
        </button>
      </section>
    );
  }

  if (recovery) {
    return (
      <section className="stack auth" style={{ maxWidth: 420 }}>
        <div>
          <Logo size={56} className="logo-hero" />
          <h1>Choose a new password</h1>
          <p className="lede">
            For the SoundLayer ID {recovery.email ? <strong>{recovery.email}</strong> : "from your reset link"}. Your library and linked
            speakers are unchanged.
          </p>
        </div>
        <form className="card stack" onSubmit={update} aria-busy={busy}>
          <div className="field">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              className="input"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span className="hint">At least 8 characters.</span>
          </div>
          <div className="field">
            <label htmlFor="confirm-password">Confirm new password</label>
            <input
              id="confirm-password"
              className="input"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          {error && <Notice kind="error">{error}</Notice>}
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? "One moment…" : "Update password"}
          </button>
          <button
            className="btn btn-quiet btn-block"
            type="button"
            disabled={busy}
            onClick={() => void signOut().then(() => navigate(signinPath, { replace: true }))}
          >
            Keep my current password and sign out
          </button>
        </form>
      </section>
    );
  }

  if (session === undefined) {
    return (
      <section className="stack">
        <Progress label="Checking your session…" />
      </section>
    );
  }

  const failed = timedOut || exchangeFailed;
  const waiting = tokensInUrl && !linkError && !failed;
  if (waiting) {
    return (
      <section className="stack">
        <Progress label="Checking your reset link…" />
      </section>
    );
  }

  // No valid recovery: the link was bad, was already used, timed out, or
  // this page was opened without a link (possibly by someone already
  // signed in). Say so and offer a new link; never show the password form.
  const title = linkError ? "This reset link cannot be used" : failed ? "This reset link did not work" : "Reset your password";
  const explanation = linkError
    ? linkError.message
    : failed
      ? "The link did not sign you in for a password change. It may have expired or been used already."
      : session
        ? "You are signed in, but this page was not opened from a reset link. Request a new link if you want to change your password."
        : "Enter the email for your SoundLayer ID and we will send a reset link.";
  return (
    <section className="stack auth" style={{ maxWidth: 420 }}>
      <div>
        <Logo size={56} className="logo-hero" />
        <h1>{title}</h1>
        <p className="lede">{explanation}</p>
      </div>
      <form className="card stack" onSubmit={requestNew} aria-busy={busy}>
        <div className="field">
          <label htmlFor="recover-email">Email</label>
          <input
            id="recover-email"
            className="input"
            type="email"
            autoComplete="email"
            required
            value={requestEmail}
            onChange={(e) => setRequestEmail(e.target.value)}
          />
        </div>
        {requestError && <Notice kind="error">{requestError}</Notice>}
        {requestInfo && <Notice kind="ok">{requestInfo}</Notice>}
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? "One moment…" : "Send a new reset link"}
        </button>
      </form>
      <p className="small muted" style={{ textAlign: "center", margin: 0 }}>
        <Link to={signinPath}>Back to sign in</Link>
      </p>
    </section>
  );
}
